import React, { useEffect } from 'react';
import { useDragLayer, XYCoord } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { PersonProps } from 'Components/common/components/PeopleCardLarge';
import PeopleCardLargeDrag from 'Components/common/components/PeopleCardLargeDrag';
import { selectGroupDrawerOpen } from 'Selector/GroupsSelector';
import { GROUP_DETAIL_ROUTE } from 'Utils/Constants';
import { dragInteractionStyle } from 'Utils/performance/renderOptimizations';
import { actions as uiActions } from '../../../Slice/UISlice';

const getDragLayerStyles = (initialOffset: XYCoord | null, currentOffset: XYCoord | null) => {
  if (!initialOffset || !currentOffset) {
    return {
      display: 'none',
    };
  }
  const { x, y } = currentOffset;
  const transform = `translate(${x}px, ${y}px)`;
  return {
    transform,
    WebkitTransform: transform,
  };
};

const SCROLL_THRESHOLD = 150;
const SCROLL_SPEED = 5;
const SCROLL_INTERVAL = 15;

const CustomDragLayer = function CustomDragLayer() {
  const dispatch = useDispatch();
  const groupDrawervisible = useSelector(selectGroupDrawerOpen);
  const drawerElement = document.querySelector('.people-group-drawer .groups-wrapper') as HTMLElement;
  const {
    itemType,
    item,
    isDragging,
    initialOffset,
    currentOffset,
  }: {
    itemType: string | symbol | null,
    item: PersonProps,
    isDragging: boolean,
    initialOffset: XYCoord | null,
    currentOffset: XYCoord | null
  } = useDragLayer((monitor) => ({
    itemType: monitor.getItemType(),
    item: monitor.getItem(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  useEffect(() => {
    if (item?.membersActive || item?.membersActive === undefined) {
      dispatch(uiActions.groupsTitleName(item?.groupTitle || ''));
      dispatch(uiActions.setGroupCategory(
        { category: item?.categoryData?.prevCategory, stage: item?.categoryData?.prevStage },
      ));
      if (isDragging) {
        dispatch(uiActions.isPersonDrag(true));
      } else {
        dispatch(uiActions.isPersonDrag(false));
      }
    }
  }, [dispatch, item, isDragging]);

  useEffect(() => {
    let scrollInterval: NodeJS.Timeout | null = null;
    const pathName = window.location.pathname;
    if (isDragging && currentOffset && pathName.includes(GROUP_DETAIL_ROUTE)) {
      const { y } = currentOffset;
      const viewportHeight = window.innerHeight;
      if (groupDrawervisible) {
        if (drawerElement) {
          const drawerRect = drawerElement.getBoundingClientRect();
          if (y > drawerRect.top + drawerRect.height - SCROLL_THRESHOLD) {
            scrollInterval = setInterval(() => {
              drawerElement.scrollBy(0, SCROLL_SPEED);
            }, SCROLL_INTERVAL);
          } else if (y < drawerRect.top + SCROLL_THRESHOLD) {
            scrollInterval = setInterval(() => {
              drawerElement.scrollBy(0, -SCROLL_SPEED);
            }, SCROLL_INTERVAL);
          }
        }
      } else if (y > viewportHeight - SCROLL_THRESHOLD) {
        scrollInterval = setInterval(() => {
          window.scrollBy(0, SCROLL_SPEED);
        }, SCROLL_INTERVAL);
      } else if (y < SCROLL_THRESHOLD + 150) {
        scrollInterval = setInterval(() => {
          window.scrollBy(0, -SCROLL_SPEED);
        }, SCROLL_INTERVAL);
      }
    }

    return () => {
      if (scrollInterval) clearInterval(scrollInterval);
    };
  }, [currentOffset, groupDrawervisible, isDragging]);

  const renderItem = () => {
    switch (itemType) {
      case 'PEOPLE':
        return <PeopleCardLargeDrag {...item} dragCheck />;

      default:
        return null;
    }
  };

  if (!isDragging) {
    return null;
  }

  return (
    <div className="drag-appeaser" style={dragInteractionStyle}>
      <div style={{ ...getDragLayerStyles(initialOffset, currentOffset), ...dragInteractionStyle }}>
        {renderItem()}
      </div>
    </div>
  );
};

export default CustomDragLayer;
